import { Box, Typography, Card, CardContent, CardActionArea, Chip } from "@mui/material";
import Header from "../components/header";
import { useNavigate } from "react-router-dom";
import ExtensionIcon from "@mui/icons-material/Extension";
import ViewKanbanIcon from "@mui/icons-material/ViewKanban";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

const projectList = [
  {
    id: "supersimplescrum",
    title: "SuperSimpleScrum",
    subtitle: "Scrum. Minus the bureaucracy.",
    description:
      "A lightweight scrum board built for developers who want to execute fast. No story points, no overhead — just Backlog, Active, and Done.",
    icon: <ViewKanbanIcon sx={{ fontSize: 40 }} />,
    tags: ["Web App", "React", "Scrum"],
    route: "/projects/supersimplescrum",
  },
  {
    id: "whytwice",
    title: "WhyTypeTwice",
    subtitle: "useState, done in one.",
    description:
      "A VS Code extension that auto-completes React useState destructuring. Type const [name, and the extension handles the rest — setter, useState(), and cursor placement.",
    icon: <ExtensionIcon sx={{ fontSize: 40 }} />,
    tags: ["VS Code Extension", "TypeScript", "React"],
    route: "/projects/whytwice",
  },
];

const Projects = () => {
  const navigate = useNavigate();

  return (
    <Box sx={{ backgroundColor: "#f5f7fa", minHeight: "100vh" }}>
      <Header />

      {/* Hero */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
          color: "white",
          py: { xs: 8, md: 10 },
          px: 3,
          textAlign: "center",
        }}
      >
        <Typography
          variant="h2"
          sx={{
            fontFamily: "Roboto Slab",
            fontWeight: 700,
            fontSize: { xs: "2rem", md: "3rem" },
            mb: 2,
          }}
        >
          Projects
        </Typography>
        <Typography
          variant="h6"
          sx={{ fontFamily: "Roboto", opacity: 0.9, fontSize: { xs: "1rem", md: "1.25rem" } }}
        >
          Things I've built — shipped and in use.
        </Typography>
      </Box>

      {/* Project Cards */}
      <Box sx={{ py: { xs: 6, md: 10 }, px: 3 }}>
        <Box
          sx={{
            maxWidth: "900px",
            margin: "0 auto",
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" },
            gap: 4,
          }}
        >
          {projectList.map((project) => (
            <Card
              key={project.id}
              sx={{
                borderRadius: 3,
                boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
                transition: "transform 0.25s ease, box-shadow 0.25s ease",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: "0 12px 28px rgba(0,0,0,0.14)",
                },
              }}
            >
              <CardActionArea
                onClick={() => navigate(project.route)}
                sx={{ height: "100%", display: "flex", flexDirection: "column", alignItems: "stretch" }}
              >
                <CardContent sx={{ p: 4, flex: 1, display: "flex", flexDirection: "column", gap: 2 }}>
                  {/* Icon + Title */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Box
                      sx={{
                        display: "inline-flex",
                        p: 1.5,
                        borderRadius: 2,
                        backgroundColor: "#ecfdf5",
                        color: "#10b981",
                        flexShrink: 0,
                      }}
                    >
                      {project.icon}
                    </Box>
                    <Box>
                      <Typography variant="h6" sx={{ fontFamily: "Roboto Slab", fontWeight: 700, lineHeight: 1.2 }}>
                        {project.title}
                      </Typography>
                      <Typography variant="body2" sx={{ color: "#10b981", fontWeight: 500 }}>
                        {project.subtitle}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Description */}
                  <Typography variant="body2" sx={{ color: "#555", lineHeight: 1.7, flex: 1 }}>
                    {project.description}
                  </Typography>

                  {/* Tags + Arrow */}
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                      {project.tags.map((tag) => (
                        <Chip
                          key={tag}
                          label={tag}
                          size="small"
                          sx={{ backgroundColor: "#ecfdf5", color: "#047857", fontWeight: 500, fontSize: "0.7rem" }}
                        />
                      ))}
                    </Box>
                    <ArrowForwardIcon sx={{ color: "#10b981", fontSize: "1.2rem" }} />
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default Projects;
